import {ActionIcon, Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import MonacoEditor from 'llm/monaco/MonacoEditor';
import {MONACO_LANGUAGES, MonacoLanguage} from 'llm/monaco/MonacoTypes';
import {GRAY_LIGHT, WHITE} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {FileViewerBody} from './FileViewerBody';

export type PreviewContent =
  | {kind: 'code'; code: string; language: string; label: string}
  | {kind: 'file'; url: string; downloadUrl: string; filename: string; mimeType: string; label: string};

interface PreviewPaneProps {
  content: PreviewContent;
  onClose: () => void;
}

function isMonacoLanguage(language: string): language is MonacoLanguage {
  return (MONACO_LANGUAGES as ReadonlyArray<string>).includes(language);
}

const TEXT_MIME_TYPES = new Set([
  'text/plain',
  'application/json',
  'text/markdown',
  'text/csv',
  'text/tsv',
  'text/x-python-script',
]);

function isTextMimeType(mimeType: string): boolean {
  return TEXT_MIME_TYPES.has(mimeType);
}

export function PreviewPane({content, onClose}: PreviewPaneProps): JSX.Element {
  const handleDownload = useCallback(() => {
    if (content.kind === 'file') {
      window.open(content.downloadUrl);
    }
  }, [content]);

  const handleCopy = useCallback(async () => {
    if (content.kind === 'file') {
      const response = await fetch(content.url);
      const text = await response.text();
      await copyToClipboard(text);
    }
  }, [content]);

  const showDownloadButton = content.kind === 'file';
  const showCopyButton = content.kind === 'file' && isTextMimeType(content.mimeType);

  return (
    <div className={css(styles.container)}>
      <div role="banner" className={css(styles.header)}>
        <span className={css(styles.label)}>{content.label}</span>
        <div className={css(styles.actions)}>
          {showDownloadButton && (
            <Tooltip label="Download">
              <ActionIcon
                aria-label="Download file"
                variant="outline"
                size="sm"
                color="gray"
                onClick={handleDownload}
              >
                <BenchlingIcon icon="download" />
              </ActionIcon>
            </Tooltip>
          )}
          {showCopyButton && (
            <Tooltip label="Copy">
              <ActionIcon
                aria-label="Copy to clipboard"
                variant="outline"
                size="sm"
                color="gray"
                onClick={handleCopy}
              >
                <BenchlingIcon icon="copy" />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label="Close">
            <ActionIcon aria-label="Close preview" variant="outline" size="sm" color="gray" onClick={onClose}>
              <BenchlingIcon icon="close" />
            </ActionIcon>
          </Tooltip>
        </div>
      </div>
      <div className={css(styles.body)}>
        {content.kind === 'code' ? (
          <CodeViewerBody code={content.code} language={content.language} />
        ) : (
          <FileViewerBody url={content.url} mimeType={content.mimeType} />
        )}
      </div>
    </div>
  );
}

function CodeViewerBody({code, language}: {code: string; language: string}): JSX.Element {
  if (isMonacoLanguage(language)) {
    return (
      <MonacoEditor
        width="100%"
        height="100%"
        value={code}
        language={language}
        theme="vs"
        options={{readOnly: true, minimap: {enabled: false}}}
      />
    );
  }

  return (
    <pre className={css(styles.code)}>
      <code>{code}</code>
    </pre>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: WHITE,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    fontSize: 14,
    fontWeight: 600,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    flexShrink: 0,
  },
  label: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  },
  body: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
  },
  code: {
    margin: 0,
    padding: 16,
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
  },
});
